/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */

import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import UntitledPage from '~components/page/UntitledPage';
import Spinner from '~components/skeleton/Spinner';

import usePathName from '../usePathName';

const LOADER_DELAY_MS = 250;

/**
 * Create a "page loading" div. The spinner won't display until after 250 ms of waiting, so it won't flash the screen.
 * But after 250ms, the page is taking too long to import so we display the spinner.
 * This displays the longest on checkout pages. But it will display on all pages, not just subroutes.
 *
 * @param {object} props
 * @param {boolean} props.isFullPage - true if this not a subroute
 * @returns {React.ReactNode} a div with a spinner
 */
const PageLoader = ({ isFullPage }) => {
    // This is here so we reset the spinner hide/show when navigating between pages
    const pathName = usePathName();

    const [show, setShow] = useState(false);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setShow(false);
        const timeout = setTimeout(() => setShow(true), LOADER_DELAY_MS);
        return () => {
            clearTimeout(timeout);
        };
    }, [setShow, pathName]);

    let spinnerClass = 'page-loader';
    if (show) {
        spinnerClass += ' show';
    }
    const spinner = (
        <div className={spinnerClass}>
            <Spinner size={'100%'} />
        </div>
    );
    if (isFullPage) {
        return (
            <UntitledPage>
                <div className="container page-loader-container">{spinner}</div>
            </UntitledPage>
        );
    }
    return <div className="page-loader-container">{spinner}</div>;
};

PageLoader.propTypes = {
    isFullPage: PropTypes.bool,
};
export default PageLoader;
